// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePersona } from '../../hooks/usePersona';
import { useAuth } from '../../hooks/useAuth';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useSiteApprovals } from '../../hooks/useSiteApprovals';
import { pendingReviewPlans } from '../../services/planSelectors';
import { useSiteTime } from '../../hooks/useSiteTime';
import { railItemsFor } from './navModel';
import { useFlagOn } from '../../hooks/useFlagOn';
import { useKillSwitch } from '../../hooks/useKillSwitch';

/**
 * 56px left icon rail. Items are persona-gated; active state derives from the
 * current route prefix. Replaces the mock's `*NavBg` derived props.
 */
export default function IconRail() {
  const { persona, isAdmin } = usePersona();
  const { permissions } = useAuth();
  const { siteId, isFleet } = useActiveSite();
  const location = useLocation();
  const navigate = useNavigate();

  const notesAudit = useFlagOn('notesAuditEnabled');
  // Kill switch (default on): shown from the default while LaunchDarkly answers, so the
  // item doesn't flash in or stay hidden on a stale read; it disappears only when the
  // flag is explicitly off for the identity that is ready.
  const workOrders = useKillSwitch('workOrdersEnabled');

  const items = railItemsFor(persona, siteId, isFleet, permissions).filter(
    item =>
      (!item.adminOnly || isAdmin) &&
      (item.section !== 'records' || notesAudit) &&
      (item.section !== 'workOrders' || workOrders)
  );

  const { approvals } = useSiteApprovals();
  const { siteZone } = useSiteTime();
  const pendingApprovalsCount = pendingReviewPlans(approvals, undefined, siteZone).length;

  if (isFleet || location.pathname.startsWith('/fleet')) return null;

  return (
    <nav className="helios-rail" aria-label="Primary">
      {items.map(item => {
        const Icon = item.icon;
        const testIdSection = item.section.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
        const matches = Array.isArray(item.match) ? item.match : [item.match];
        const active = matches.some(
          m => location.pathname === m || location.pathname.startsWith(m + '/')
        );
        const badgeCount = item.section === 'optimization' ? pendingApprovalsCount : 0;
        return (
          <button
            key={item.section}
            type="button"
            className={`helios-rail-item${active ? ' active' : ''}`}
            data-testid={`nav-${testIdSection}`}
            aria-current={active ? 'page' : undefined}
            title={item.label}
            onClick={() => navigate(item.to)}
          >
            <span className="helios-rail-icon">
              <Icon size={24} />
              {badgeCount > 0 && (
                <span className="helios-rail-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
              )}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
